const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

// Shared filter vocabulary for every widget: one preset in, one set of API
// params out, so the shell passes the same filters to all widgets and
// react-query dedupes identical requests between them.
//
// `preset` is what the operator picked ("7d"). `range` is the resolved
// { start_at, end_at } every widget queries with, plus `states` when the page
// is narrowed to some of the brand's states. They are separate fields:
// folding one into the other is what used to leave the picker showing a
// stale label while the data moved. The range carries the states because
// it is what every read is built from: a read narrowed to Pennsylvania is a
// different read, with its own cache entry (rangeKey).

export const RANGE_PRESETS = Object.freeze([
  // The previous whole local day. Not the default: copies made late in the
  // evening are still pending until about 90 minutes after midnight.
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: 'custom', label: 'Custom range' },
])

export const DEFAULT_FILTERS = Object.freeze({
  preset: '30d',
  customStart: '',
  customEnd: '',
  compare: true,
  // State keys (a Kambi regulation, else an offering); empty is every state.
  states: Object.freeze([]),
})

// The URL's `states` parameter as a list of keys, and back: comma-separated,
// in the order picked, repeats dropped.
export function parseStatesParam(value) {
  return [...new Set(String(value || '').split(',').map((key) => key.trim()).filter(Boolean))]
}

export function statesParam(states) {
  return (states || []).join(',')
}

// A range narrowed to some states: the `states` parameter every analytics
// read takes, or the range unchanged for every state.
export function withStates(range, states) {
  if (!range || !states?.length) return range
  return { ...range, states: statesParam(states) }
}

// The part of a query key a range decides: its bounds and its states.
export function rangeKey(range) {
  return [range?.start_at, range?.end_at, range?.states || '']
}

const DAY_MS = 86_400_000

function startOfLocalDay(dateString) {
  const [year, month, day] = dateString.split('-').map(Number)
  return new Date(year, month - 1, day, 0, 0, 0, 0)
}

function localDateString(date) {
  const pad = (value) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

// The YYYY-MM-DD key of a local calendar day, and back. The URL and the
// custom-range picker speak in these keys.
export function localDateKey(date) {
  return localDateString(date)
}

export function parseDateKey(key) {
  if (!key || !ISO_DATE_PATTERN.test(key)) return null
  const date = startOfLocalDay(key)
  return Number.isNaN(date.getTime()) ? null : date
}

// "Sep 1 – Sep 7, 2026", or with both years when the range crosses one.
export function formatDateRangeLabel(startKey, endKey) {
  const start = parseDateKey(startKey)
  const end = parseDateKey(endKey)
  if (!start || !end) return ''
  const sameYear = start.getFullYear() === end.getFullYear()
  const first = start.toLocaleDateString('en-US', sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' })
  const last = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  return `${first} – ${last}`
}

export function rangeParamsFor(filters, now = new Date()) {
  if (filters.preset === 'custom') {
    const start = parseDateKey(filters.customStart)
    const end = parseDateKey(filters.customEnd)
    if (!start || !end) return null
    end.setDate(end.getDate() + 1)
    if (end <= start) return null
    return { start_at: start.toISOString(), end_at: end.toISOString() }
  }
  if (filters.preset === 'yesterday') {
    // Local midnight to local midnight, like a custom range, so a day is a
    // day even across a daylight-saving change.
    const end = startOfLocalDay(localDateString(now))
    const start = new Date(end)
    start.setDate(start.getDate() - 1)
    return { start_at: start.toISOString(), end_at: end.toISOString() }
  }
  const preset = RANGE_PRESETS.find((item) => item.key === filters.preset)
    || RANGE_PRESETS.find((item) => item.key === DEFAULT_FILTERS.preset)
  const end = new Date(now)
  const start = new Date(now)
  start.setDate(start.getDate() - preset.days)
  return { start_at: start.toISOString(), end_at: end.toISOString() }
}

// The bucket a range's series is drawn in: hours for a range of two days or
// less ("Yesterday", a one- or two-day custom range), where a daily line
// would be one or two points; days otherwise. Hourly rows are UTC hours, so
// a local day is drawn across the UTC dates it spans without a seam.
export const HOURLY_MAX_DAYS = 2
export const HOURLY_OFFERED_MAX_DAYS = 7

export function granularityFor(range) {
  const days = daysInRange(range)
  return days > 0 && days <= HOURLY_MAX_DAYS ? 'hour' : 'day'
}

// Whole days a range covers, rounded, so a local day that is 23 or 25 hours
// long still counts as one.
export function daysInRange(range) {
  if (!range) return 0
  return Math.round((new Date(range.end_at) - new Date(range.start_at)) / DAY_MS)
}

function isLocalMidnight(date) {
  return date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0 && date.getMilliseconds() === 0
}

// The same length of time, ending where the range starts. A range of whole
// local days (Yesterday, a custom range) steps back by calendar days, so the
// day before a daylight-saving change still starts at midnight rather than
// an hour off; any other range steps back by its duration.
// The previous period keeps the range's states: a state is compared with
// itself.
export function previousRangeFor(range) {
  if (!range) return null
  const start = new Date(range.start_at)
  const end = new Date(range.end_at)
  const duration = end.getTime() - start.getTime()
  if (!Number.isFinite(duration) || duration <= 0) return null
  const states = range.states ? { states: range.states } : {}
  if (isLocalMidnight(start) && isLocalMidnight(end)) {
    const previousStart = new Date(start)
    previousStart.setDate(previousStart.getDate() - daysInRange(range))
    return { start_at: previousStart.toISOString(), end_at: start.toISOString(), ...states }
  }
  return { start_at: new Date(start.getTime() - duration).toISOString(), end_at: start.toISOString(), ...states }
}

// The preset's name. A custom range says "Custom range" here; its dates are
// on the picker beside it, not repeated on the selector.
export function rangeLabelFor(filters) {
  if (filters.preset === 'custom') return 'Custom range'
  return RANGE_PRESETS.find((item) => item.key === filters.preset)?.label || 'Last 30 days'
}
